import axios from 'axios';
import { Product, Category, ParcelDay } from '../types';
import { logger } from '../utils/logger';

// Create axios instance with default config
// Use environment-specific base URL for staging/production, relative URL for development
export const api = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '/api', // Environment-aware base URL
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// Add a request interceptor
api.interceptors.request.use(
    (config) => {
        // Public endpoints (e.g. delivery validate-address, by-postcode) must not send auth;
        // sending an admin token can cause server errors (ERR_HTTP2_PROTOCOL_ERROR) on some hosts.
        const skipAuth = (config as any).skipAuth === true;
        const token = skipAuth ? null : localStorage.getItem('token');
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        const fullUrl = `${config.baseURL}${config.url}`;
        console.log('API Request Details:', {
            method: config.method,
            url: fullUrl,
            baseURL: config.baseURL,
            data: config.data,
            params: config.params,
            headers: config.headers,
        });
        logger.info(`API Request: ${config.method?.toUpperCase()} ${fullUrl}`, {
            method: config.method,
            url: fullUrl,
            baseURL: config.baseURL,
            data: config.data,
            params: config.params,
            headers: config.headers,
        });
        return config;
    },
    (error) => {
        console.error('API Request Error:', error);
        logger.error('API Request Error:', error);
        return Promise.reject(error);
    }
);

// Add a response interceptor
api.interceptors.response.use(
    (response) => {
        console.log('API Response:', {
            status: response.status,
            data: response.data,
            headers: response.headers,
        });
        logger.info(`API Response: ${response.status}`, response);
        return response;
    },
    (error) => {
        console.error('API Response Error:', {
            error,
            response: error.response,
            message: error.message,
            status: error.response?.status,
            data: error.response?.data
        });
        if (error.response?.status === 401) {
            // Handle unauthorized access
            localStorage.removeItem('token');
            // window.location.href = '/login'; // Disabled for development
            console.warn('Received 401 Unauthorized. Token cleared. No redirect during development.');
        }
        logger.error('API Response Error:', error);
        return Promise.reject(error);
    }
);

// Admin API endpoints
export const getProducts = async (): Promise<Product[]> => {
    const response = await api.get('/v1/admin/products');
    // Products API has nested structure: { data: { data: [...] } }
    return response.data.data?.data || [];
};

export const getProduct = async (id: number): Promise<Product> => {
    const response = await api.get<{ data: Product }>(`/v1/admin/products/${id}`);
    return response.data.data;
};

export const getCategories = async (): Promise<Category[]> => {
    const response = await api.get<{ data: Category[] }>('/v1/admin/categories');
    return response.data.data;
};

export const getCategory = async (id: number): Promise<Category> => {
    const response = await api.get<{ data: Category }>(`/v1/admin/categories/${id}`);
    return response.data.data;
};

export const getParcelDays = async (): Promise<ParcelDay[]> => {
    const response = await api.get('/v1/admin/parcel-days');
    return response.data.data;
};

export const getParcelDay = async (id: number): Promise<ParcelDay> => {
    const response = await api.get<{ data: ParcelDay }>(`/v1/admin/parcel-days/${id}`);
    return response.data.data;
};

/**
 * Get bundle details by IDs for checkout display
 */
export const getBundlesByIds = async (bundleIds: number[]): Promise<any> => {
    const response = await api.post('/v1/product-bundles/by-ids', {
        bundle_ids: bundleIds
    });
    return response.data;
};

/**
 * Submit volunteer application
 */
export const submitVolunteerApplication = async (volunteerData: any): Promise<any> => {
    const response = await api.post('/v1/volunteers', volunteerData);
    return response.data;
}; 